import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TASHKENT, loadYandexMaps, type LatLng, type YMap, type YMaps } from '@dorixona/ui'
import { useMapKeySource } from '../../api/mapsKey'
import type { Store } from './api'

/** Что карте нужно от аптеки: где поставить булавку и как её подписать. */
export type MapStore = Pick<Store, 'id' | 'name' | 'lat' | 'lng'>

export interface StoresMapProps {
  /** Аптеки в порядке списка: номер на булавке — номер карточки. */
  stores: MapStore[]
  selected: number | null
  onSelect: (id: number) => void
  /** Где покупатель: синяя точка «Вы здесь». */
  me?: { lat: number; lng: number } | null
  /** Растёт — карта подводит к покупателю (кнопка «Где я»). */
  meFocus?: number
}

/** Точка «Вы здесь»: та же привязка левым верхним углом, `.me-at` центрирует её. */
const ME = '<div class="me-at"><div class="me-dot"></div></div>'

type State = 'loading' | 'ready' | 'failed'

/**
 * Булавка с номером карточки. Яндекс ставит разметку метки левым верхним
 * углом в точку; `.lpin-at` сдвигает её так, чтобы в точку смотрело остриё.
 */
function pin(number: number, on: boolean): string {
  const state = on ? ' on' : ''
  return `<div class="lpin-at${state}"><div class="lpin${state}"><span>${number}</span></div></div>`
}

/**
 * Карта аптек сети на Яндекс Картах (JS API 2.1, общий загрузчик в
 * `@dorixona/ui`). Выбранная булавка — жёлтая и крупнее. Нажатие на булавку
 * выбирает аптеку, выбор в списке подводит к ней карту.
 */
export function StoresMap({ stores, selected, onSelect, me = null, meFocus = 0 }: StoresMapProps) {
  const { t } = useTranslation('common')
  const box = useRef<HTMLDivElement>(null)
  const api = useRef<YMaps | null>(null)
  const map = useRef<YMap | null>(null)
  const [state, setState] = useState<State>('loading')
  const keySource = useMapKeySource()
  const select = useRef(onSelect)
  useEffect(() => {
    select.current = onSelect
  })

  // Карта создаётся один раз на экран и убирается вместе с ним.
  useEffect(() => {
    let alive = true
    loadYandexMaps(keySource).then(
      (ymaps) => {
        if (!alive) return
        const view = new ymaps.Map(
          box.current as HTMLDivElement,
          { center: TASHKENT, zoom: 11, controls: ['zoomControl'] },
          { suppressMapOpenBlock: true },
        )
        // Колесо и палец листают страницу, а не карту: список под ней.
        view.behaviors.disable('scrollZoom')
        api.current = ymaps
        map.current = view
        setState('ready')
      },
      () => {
        if (alive) setState('failed')
      },
    )
    return () => {
      alive = false
      map.current?.destroy()
      map.current = null
    }
  }, [keySource])

  const placed = stores.filter((store) => store.lat !== null && store.lng !== null)
  const meLat = me?.lat
  const meLng = me?.lng
  // Покупатель тоже в кадре: видно и его, и аптеки вокруг.
  const points = [
    ...placed.map((store) => [store.lat, store.lng] as LatLng),
    ...(meLat !== undefined && meLng !== undefined ? [[meLat, meLng] as LatLng] : []),
  ]
  const where = points.map((point) => point.join(':')).join('|')

  // Булавки: перерисовываем, когда меняется список или выбор.
  useEffect(() => {
    if (state !== 'ready') return
    const ymaps = api.current as YMaps
    const view = map.current as YMap
    view.geoObjects.removeAll()
    stores.forEach((store, index) => {
      if (store.lat === null || store.lng === null) return
      const on = store.id === selected
      const mark = new ymaps.Placemark(
        [store.lat, store.lng],
        { hintContent: store.name },
        {
          iconLayout: ymaps.templateLayoutFactory.createClass(pin(index + 1, on)),
          iconShape: on
            ? { type: 'Circle', coordinates: [0, -25], radius: 18 }
            : { type: 'Circle', coordinates: [0, -21], radius: 15 },
          zIndex: on ? 1000 : 0,
        },
      )
      mark.events.add('click', () => {
        select.current(store.id)
      })
      view.geoObjects.add(mark)
    })
    if (meLat !== undefined && meLng !== undefined) {
      view.geoObjects.add(
        new ymaps.Placemark(
          [meLat, meLng],
          { hintContent: t('map.me') },
          {
            iconLayout: ymaps.templateLayoutFactory.createClass(ME),
            iconShape: { type: 'Circle', coordinates: [0, 0], radius: 10 },
            zIndex: 500,
          },
        ),
      )
    }
  }, [state, stores, selected, meLat, meLng, t])

  // «Где я» — подводим карту к покупателю.
  useEffect(() => {
    if (state !== 'ready' || meFocus === 0 || meLat === undefined || meLng === undefined) return
    const view = map.current as YMap
    view.setCenter([meLat, meLng], Math.max(view.getZoom(), 15), { duration: 300 })
  }, [state, meFocus, meLat, meLng])

  // Новый список (поиск, фильтр) — показываем все его аптеки разом.
  useEffect(() => {
    if (state !== 'ready' || where === '') return
    const view = map.current as YMap
    const rows = where.split('|').map((row) => row.split(':').map(Number) as LatLng)
    const lats = rows.map(([lat]) => lat)
    const lngs = rows.map(([, lng]) => lng)
    if (rows.length === 1) {
      view.setCenter(rows[0] as LatLng, 14)
      return
    }
    view.setBounds(
      [
        [Math.min(...lats), Math.min(...lngs)],
        [Math.max(...lats), Math.max(...lngs)],
      ],
      { checkZoomRange: true, zoomMargin: 32 },
    )
  }, [state, where])

  // Выбрали аптеку в списке — подводим к ней карту.
  const target = placed.find((store) => store.id === selected)
  const lat = target?.lat
  const lng = target?.lng
  useEffect(() => {
    if (state !== 'ready' || lat === undefined || lng === undefined) return
    const view = map.current as YMap
    view.setCenter([lat as number, lng as number], Math.max(view.getZoom(), 14), {
      duration: 300,
    })
  }, [state, lat, lng])

  return (
    <div
      ref={box}
      id="storesMap"
      role="group"
      aria-label={t('map.stores')}
      aria-busy={state === 'loading'}
      data-state={state}
    >
      {state === 'failed' ? <span className="map-failed">{t('map.failed')}</span> : null}
    </div>
  )
}
